import React, { useState, useEffect } from 'react';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import InvoiceForm from '../components/InvoiceForm';
import { useLocation, useNavigate } from 'react-router-dom';
import { Stack } from '@mui/material';
import { useCreateInvoiceHandler } from '../dataHandlers/createInvoiceHandler';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { CREATE_PURCHASE_INVOICE, CREATE_SALE_INVOICE } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function CreateInvoicePage({ isPurchase = false }) {
  const { eInvoiceForm } = useTranslatedMessages();
  const { page } = eInvoiceForm;
  useDocumentTitle(isPurchase ? page.title_purchase : page.title_sales);
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();

  useEffect(() => {
    if (loading) {
      return;
    }
    const createPermission = isPurchase ? CREATE_PURCHASE_INVOICE : CREATE_SALE_INVOICE;
    if (!hasEInvoicePermission(createPermission)) {
      showActionDeniedSnackbar();
      navigate(isPurchase ? '/purchases/documents?tab=invoices' : '/sales/documents?tab=invoices', { replace: true });
    }
  }, [isPurchase, loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  const onInvoiceCreated = (payload) => {
    navigate(isPurchase ? '/purchases/documents?tab=invoices' : '/sales/documents?tab=invoices', {
      replace: true,
      state: {},
    });
  };

  const { onSubmit, error, isPending } = useCreateInvoiceHandler({ onSuccess: onInvoiceCreated, isPurchase });

  // Reset isSubmitting when mutation completes
  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const document = location.state?.document;

  const onSaveForm = async (data) => {
    setIsSubmitting(true);
    const payload = {
      ...data,
      status: 'active',
      proforma_invoice_uuid: location.state?.document?.id || null,
    };

    onSubmit(payload);
  };

  const onCancel = async () => {
    navigate(
      location.state?.from || (isPurchase ? '/purchases/documents?tab=invoices' : '/sales/documents?tab=invoices'),
      { replace: true, state: {} },
    );
  };

  return (
    <Stack direction="column" sx={{ mt: '-2rem' }}>
      <InvoiceForm
        initialData={document || {}}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isDraftButtonEnabled={location.state?.isDraftButtonEnabled}
        isPurchase={isPurchase}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
